import { useEffect, useState } from "react";
import { Check, Handshake } from "lucide-react";
import { usePage } from "../../hooks/usePage";
import { Loading, PageHero } from "../../components/site/Blocks";
import EnquiryDialog from "../../components/sponsorship/EnquiryDialog";
import VerificationTestCard from "../../components/payments/VerificationTestCard";
import SponsorCheckoutDialog from "../../components/sponsorship/SponsorCheckoutDialog";
import { api, errMsg } from "../../lib/api";
import { toast } from "sonner";
import { SponsorBanners, SponsorWall } from "../../components/site/SponsorShowcase";
import { Icon } from "../../lib/icons";
import { moneyShort } from "../../lib/format";

const TIER_BTN = { platinum: "bg-navy hover:bg-navy-dark", gold: "bg-[#8A6A1F] hover:bg-[#735818]", silver: "bg-slate-600 hover:bg-slate-700", bronze: "bg-[#8A4B24] hover:bg-[#733E1D]" };

const PackageCard = ({ p, onChoose, onBuy }) => (
  <li className="flex flex-col rounded-lg border border-[#E8DFC9] bg-[#FBF8F2] p-6" data-testid={`sponsor-package-${p.tier || p.id}`}>
    <Icon name={p.icon} size={34} className="mx-auto text-navy" />
    <h3 className="mt-3 text-center text-xl font-bold text-navy">{p.name}</h3>
    <p className="text-center font-serif text-lg font-bold text-navy" data-testid={`sponsor-package-price-${p.tier || p.id}`}>{moneyShort(p.price, p.currency)}</p>
    {p.is_sample_price && <p className="text-center text-xs font-semibold uppercase tracking-wider text-burgundy">Sample price</p>}
    {p.description && <p className="mt-2 text-center text-sm text-[#4A5568]">{p.description}</p>}
    <ul className="mt-5 flex-1 space-y-2 text-sm text-[#1A202C]">
      {(p.benefits || []).map((b) => <li key={b} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-burgundy" aria-hidden="true" />{b}</li>)}
    </ul>
    <button type="button" onClick={() => onChoose(p.id)} className={`btn btn-sm mt-6 w-full text-white ${TIER_BTN[p.tier] || "bg-burgundy hover:bg-burgundy-hover"}`}
      data-testid={`become-sponsor-btn-${p.tier || p.id}`}>Become a {p.name} Sponsor</button>
    {p.price > 0 && <button type="button" onClick={() => onBuy(p.id)} className="btn-outline btn-sm mt-2 w-full" data-testid={`buy-sponsor-btn-${p.tier || p.id}`}>Purchase &amp; pay (card or check)</button>}
  </li>
);

const ROWS = [["Event tickets", (p) => p.complimentary_passes], ["Parking spaces", (p) => p.parking_spaces],
  ["Promotional booth", (p) => p.booth], ["Logo on event digital signage and promotional materials", (p) => p.logo]];
const Cell = ({ v }) => (typeof v === "number" ? <span className="font-semibold">{v}</span>
  : v ? <Check size={18} className="mx-auto text-burgundy" aria-label="Included" /> : <span className="text-slate-400" aria-label="Not included">–</span>);

const BenefitMatrix = ({ list }) => (
  <div className="mt-6 overflow-x-auto"><table className="w-full min-w-[520px] text-sm" data-testid={`benefit-matrix-${list[0].group || "package"}`}>
    <caption className="sr-only">Benefits by sponsorship level</caption>
    <thead><tr className="border-b-2 border-navy"><th scope="col" className="py-2 text-left text-navy">Benefit</th>
      {list.map((p) => <th key={p.id} scope="col" className="py-2 text-center text-navy">{p.name}<span className="block text-xs font-normal">{moneyShort(p.price, p.currency)}</span></th>)}</tr></thead>
    <tbody>{ROWS.map(([label, get]) => <tr key={label} className="border-b"><th scope="row" className="py-2 text-left font-normal">{label}</th>
      {list.map((p) => <td key={p.id} className="py-2 text-center" data-testid={`matrix-${p.tier}-${label.split(" ")[0].toLowerCase()}`}><Cell v={get(p)} /></td>)}</tr>)}</tbody>
  </table></div>
);

export default function Sponsorship() {
  const { section, loading } = usePage("sponsorship");
  const [packages, setPackages] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [dialog, setDialog] = useState({ open: false, packageId: "custom" });
  const [buy, setBuy] = useState({ open: false, packageId: "" });
  const [verify, setVerify] = useState(null);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("verify");
    if (!token) return;
    api.get(`/public/verification/${token}`).then(({ data }) => {
      if (data.kind !== "sponsorship") return;
      setVerify({ ...data, token }); setBuy({ open: true, packageId: data.package_id });
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    api.get("/public/sponsorship-packages").then((r) => setPackages(r.data));
    api.get("/public/sponsors").then((r) => setSponsors(r.data));
  }, []);
  if (loading) return <Loading />;
  const [hero, pk, custom, sp] = ["hero", "packages", "custom", "sponsors"].map(section);
  const choose = (packageId) => setDialog({ open: true, packageId });

  return (
    <div data-testid="sponsorship-page">
      {hero && <PageHero title={hero.heading} subtitle={hero.body} image={hero.image_url} testid="sponsorship-hero"><span className="mt-6 block h-0.5 w-20 bg-gold" aria-hidden="true" /></PageHero>}
      {pk && (
        <section className="container-site py-16" data-testid="sponsorship-packages">
          <h2 className="text-center text-3xl text-navy sm:text-4xl">{pk.heading}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[#4A5568]">{pk.body}</p>
          {!verify && <div className="mx-auto mt-8 max-w-sm"><VerificationTestCard kind="sponsorship" options={packages} /></div>}
          {[["package", "Sponsorship Packages"], ["event", "Event Sponsorships"]].map(([g, label]) => {
            const list = packages.filter((p) => (p.group || "package") === g);
            return list.length > 0 && (
              <div key={g} className="mt-12" data-testid={`sponsorship-group-${g}`}>
                <h3 className="text-2xl text-navy">{label}</h3>
                <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{list.map((p) => <PackageCard key={p.id} p={p} onChoose={choose} onBuy={(id) => setBuy({ open: true, packageId: id })} />)}</ul>
                <BenefitMatrix list={list} />
              </div>);
          })}
        </section>
      )}
      {custom && (
        <section className="container-site pb-16" data-testid="sponsorship-custom">
          <div className="flex flex-col items-start gap-6 rounded-lg border border-slate-200 p-6 sm:flex-row sm:items-center">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#FCE8E9] text-burgundy"><Handshake aria-hidden="true" /></span>
            <div className="flex-1"><h2 className="text-xl text-navy">{custom.heading}</h2><p className="text-[#4A5568]">{custom.body}</p></div>
            <button type="button" className="btn-outline" onClick={() => choose("custom")} data-testid="custom-sponsorship-contact-btn">{custom.button_label}</button>
          </div>
        </section>
      )}
      <SponsorBanners sponsors={sponsors} />
      <SponsorWall sponsors={sponsors} heading={sp?.heading || "Our Sponsors"} />
      <EnquiryDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} packages={packages} packageId={dialog.packageId} />
      <SponsorCheckoutDialog open={buy.open} onOpenChange={(o) => setBuy((d) => ({ ...d, open: o }))} packages={packages} packageId={buy.packageId} verify={verify} />
    </div>
  );
}
